import React from 'react';
import List from '../../cruds/List';
import View from '../../cruds/View';
import Edit from '../../cruds/Edit';
import { notNull, stringNotBlank, minValue } from '../../cruds/validators';
import Table, { Column } from '../../components/container/Table';
import { EditButton, ViewButton, DeleteButton } from '../../components/output/CrudButton';
import OutputValue from '../../components/output/OutputValue';
import OutputText, { OutputBoolean } from '../../components/output/OutputText';
import InputText from '../../components/input/InputText';
import InputNumber from '../../components/input/InputNumber';
import { booleanFormatter } from '../../functions/textFormatter';
import { fetchPost } from '../../cruds/remote';
import DownloadButton from '../../components/output/DownloadButton';
import RadioButtonGroup, { RadioButton } from '../../components/input/RadioButtonGroup';
import MainContainer from '../../components/MainContainer';
import { Switch, Route } from 'react-router-dom';
import WithRole, { withRoles } from '../../components/container/WithRole';

const ENTITY_NAME = 'phrases';
const ENTITY_ROUTE = 'h-phrases';
const TITLE = 'hPhrases.title';

const evaluate = (resource) => {
  return fetchPost(ENTITY_NAME + '/reevaluation-needed', resource);
};

const CrudEdit = props => {
  const validators = [
    stringNotBlank('code', 'hPhrases.err.code'),
    notNull('cutaneousRisk', 'hPhrases.err.cutaneousRisk'),
    notNull('teratogenRisk', 'hPhrases.err.teratogenRisk'),
    notNull('inhalationRisk', 'hPhrases.err.inhalationRisk'),
    notNull('cancerousRisk', 'hPhrases.err.cancerousRisk'),
    notNull('mutagenRisk', 'hPhrases.err.mutagenRisk'),
    notNull('atexSubstance', 'hPhrases.err.atexSubstance'),
    minValue('hazardIndex', 1, 'hPhrases.err.hazardIndex'),
  ];
  const numberFormat = /^[0-9]\d*(\.\d\d?)?$/;
  return (
    <Edit {...props}
        title={TITLE}
        entityName={ENTITY_NAME}
        entityRoute={ENTITY_ROUTE}
        entityId={props.match.params.number}
        initialValue={{ type: 'H' }}
        validators={validators}
        preSubmit={(resource) => evaluate(resource)}
    >
      <InputText name="code" label="hPhrases.code" required />
      <InputText name="description" label="hPhrases.description" />
      <InputNumber name="hazardIndex" label="hPhrases.hazardIndex" format={numberFormat} required />
      <RadioButtonGroup name="cutaneousRisk" label="hPhrases.cutaneousRisk.name" required>
        <RadioButton value="true" label="hPhrases.cutaneousRisk.true" />
        <RadioButton value="false" label="hPhrases.cutaneousRisk.false" />
      </RadioButtonGroup>
      <RadioButtonGroup name="inhalationRisk" label="hPhrases.inhalationRisk.name" required>
        <RadioButton value="true" label="hPhrases.inhalationRisk.true" />
        <RadioButton value="false" label="hPhrases.inhalationRisk.false" />
      </RadioButtonGroup>
      <RadioButtonGroup name="cancerousRisk" label="hPhrases.cancerousRisk.name" required>
        <RadioButton value="true" label="hPhrases.cancerousRisk.true" />
        <RadioButton value="false" label="hPhrases.cancerousRisk.false" />
      </RadioButtonGroup>
      <RadioButtonGroup name="mutagenRisk" label="hPhrases.mutagenRisk.name" required>
        <RadioButton value="true" label="hPhrases.mutagenRisk.true" />
        <RadioButton value="false" label="hPhrases.mutagenRisk.false" />
      </RadioButtonGroup>
      <RadioButtonGroup name="teratogenRisk" label="hPhrases.teratogenRisk.name" required>
        <RadioButton value="true" label="hPhrases.teratogenRisk.true" />
        <RadioButton value="false" label="hPhrases.teratogenRisk.false" />
      </RadioButtonGroup>
      <RadioButtonGroup name="atexSubstance" label="hPhrases.atexSubstance.name" required>
        <RadioButton value="true" label="hPhrases.atexSubstance.true" />
        <RadioButton value="false" label="hPhrases.atexSubstance.false" />
      </RadioButtonGroup>
    </Edit>
  );
};

const CrudView = props => (
  <View title={TITLE} entityName={ENTITY_NAME} entityRoute={ENTITY_ROUTE} entityId={props.match.params.number}>
    <OutputText name="code" label="hPhrases.code" />
    <OutputText name="description" label="hPhrases.description" />
    <OutputText name="hazardIndex" label="hPhrases.hazardIndex" />
    <OutputBoolean name="cutaneousRisk" label="hPhrases.cutaneousRisk.name" />
    <OutputBoolean name="inhalationRisk" label="hPhrases.inhalationRisk.name" />
    <OutputBoolean name="cancerousRisk" label="hPhrases.cancerousRisk.name" />
    <OutputBoolean name="mutagenRisk" label="hPhrases.mutagenRisk.name" />
    <OutputBoolean name="teratogenRisk" label="hPhrases.teratogenRisk.name" />
    <OutputBoolean name="atexSubstance" label="hPhrases.atexSubstance.name" />
  </View>
);

const exportButtons = (filters, context, toggleModal) => {
  return (
    <span>
      <DownloadButton from={ENTITY_NAME}
          filters={filters}
          type="application/vnd.ms-excel"
          extension="xls"
          tooltip="general.export"
          handleError={toggleModal}
      />
      <DownloadButton
          from={ENTITY_NAME}
          filters={filters}
          type="application/pdf"
          extension="pdf"
          tooltip="general.print"
          iconType="printer"
          handleError={toggleModal}
      />
    </span>
  );
};

const CrudList = props => {
  const table = <Table>
                  <Column label="hPhrases.code" orderingName="CODE">
                    <OutputValue name="code" />
                  </Column>
                  <Column label="hPhrases.description" orderingName="DESCRIPTION">
                    <OutputValue name="description" />
                  </Column>
                  <Column label="hPhrases.hazardIndex" orderingName="HAZARD_INDEX">
                    <OutputValue name="hazardIndex" />
                  </Column>
                  <Column label="hPhrases.cutaneousRisk.name" orderingName="CUTANEOUS_RISK">
                    <OutputValue name="cutaneousRisk" formatter={booleanFormatter} />
                  </Column>
                  <Column label="hPhrases.inhalationRisk.name" orderingName="INHALATION_RISK">
                    <OutputValue name="inhalationRisk" formatter={booleanFormatter} />
                  </Column>
                  <Column label="hPhrases.cancerousRisk.name" orderingName="CANCEROUS_RISK">
                    <OutputValue name="cancerousRisk" formatter={booleanFormatter} />
                  </Column>
                  <Column label="hPhrases.mutagenRisk.name" orderingName="MUTAGEN_RISK">
                    <OutputValue name="mutagenRisk" formatter={booleanFormatter} />
                  </Column>
                  <Column label="hPhrases.teratogenRisk.name" orderingName="TERATOGEN_RISK">
                    <OutputValue name="teratogenRisk" formatter={booleanFormatter} />
                  </Column>
                  <Column label="hPhrases.atexSubstance.name" orderingName="ATEX_SUBSTANCE">
                    <OutputValue name="atexSubstance" formatter={booleanFormatter} />
                  </Column>
                  <Column>
                    <WithRole roles={['A', 'B']}>
                      <EditButton entityRoute={props.match.url} />
                    </WithRole>
                    <ViewButton entityRoute={props.match.url} />
                    <WithRole roles={['A', 'B']}>
                      <DeleteButton />
                    </WithRole>
                  </Column>
                </Table>;

  return <List {...props}
                title={TITLE}
                entityName={ENTITY_NAME}
                entityRoute={ENTITY_ROUTE}
                component={table}
                filters={[
                  { name: 'fullSearch', placeholder: 'general.fullSearch' },
                  { name: 'type', defaultValue: 'H' },
                ]}
                additionalButtons={exportButtons}
                defaultOrdering="CODE"
                hideAddButton = {!withRoles(['A', 'B'])}
        />;
};

const HPhrases = props => {
  const basePath = props.match.url;
  return (
    <MainContainer>
      <Switch>
        <Route exact path={basePath} component={CrudList} />
        <Route exact path={basePath + '/view/:number'} component={CrudView} />
        <Route exact path={basePath + '/edit/:number'} render={props => <CrudEdit {...props} />} />
        <Route exact path={basePath + '/add'} render={props => <CrudEdit {...props} />} />
      </Switch>
    </MainContainer>
  );
};

export default HPhrases;



// WEBPACK FOOTER //
// ./src/main/pages/registry/HPhrases.js